import { CountryAssessments, useGetHIHAssessmentsQuery } from "@app/features/other/genericApiSlice";
import { selectModals, closeUserModal } from "@app/features/header/headerSlice";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import UserModal from "@components/userManagementModals/UserModal";
import useErrorMessage from "@utils/useErrorMessage";
import CountriesList from "./components/CountriesList";
import CountryAssessment from "./components/CountryAssessment";
import HihForumHeader from "./components/HihForumHeader";
import { HIH_FORUM_PATH } from "./constants";
import HihGlobeMap from "./map/HihGlobeMap";
import { countryForumPath, filterContinentsByYear, getAssessmentYears } from "./utils";
import "./HihInvestmentForum.css";

const YEAR_PARAM = "year";

const HihInvestmentForum = () => {
  const { isoCode: isoCodeParam } = useParams<{ isoCode?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: hihAssessments, isLoading, isError, error, isSuccess } = useGetHIHAssessmentsQuery();
  const { errorMsg } = useErrorMessage({ isError, error });

  const { userModalOpen } = useAppSelector(selectModals);
  const dispatch = useAppDispatch();

  const flatCountries = useMemo(
    () => (hihAssessments ?? []).flatMap((continent) => continent.countries),
    [hihAssessments]
  );

  const years = useMemo(() => getAssessmentYears(hihAssessments ?? []), [hihAssessments]);

  const selectedYear = useMemo(() => {
    const fromUrl = Number(searchParams.get(YEAR_PARAM));
    if (years.includes(fromUrl)) return fromUrl;
    return years[0] ?? null;
  }, [searchParams, years]);

  const yearSearch = useMemo(() => (selectedYear ? `?${YEAR_PARAM}=${selectedYear}` : ""), [selectedYear]);

  const yearGrouped = useMemo(
    () => (selectedYear ? filterContinentsByYear(hihAssessments ?? [], selectedYear) : []),
    [hihAssessments, selectedYear]
  );

  const yearCountries = useMemo(() => yearGrouped.flatMap((continent) => continent.countries), [yearGrouped]);

  const selectedCountry = useMemo(() => {
    if (!isoCodeParam) return null;
    const upper = isoCodeParam.toUpperCase();
    return flatCountries.find((c) => c.iso_code.toUpperCase() === upper) ?? null;
  }, [isoCodeParam, flatCountries]);

  useEffect(() => {
    if (!isoCodeParam || !isSuccess) return;
    if (selectedCountry) return;
    navigate(HIH_FORUM_PATH, { replace: true });
  }, [isoCodeParam, isSuccess, selectedCountry, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [isoCodeParam]);

  const handleYearChange = (year: number) => {
    setSearchParams({ [YEAR_PARAM]: String(year) });
  };

  const handleCountryClick = (country: CountryAssessments) => {
    navigate({ pathname: countryForumPath(country.iso_code), search: yearSearch });
  };

  const handleBack = () => navigate({ pathname: HIH_FORUM_PATH, search: yearSearch });

  return (
    <div className="hih-investment-forum">
      <article className="hih-forum-article">
        <HihForumHeader />

        {yearCountries.length > 0 && (
          <HihGlobeMap
            countries={yearCountries}
            selectedIsoCode={selectedCountry?.iso_code ?? null}
          />
        )}

        {isLoading ? <div className="hih-forum-status">Loading...</div> :
          errorMsg ? <div className="hih-forum-status error">{errorMsg}</div> :
            selectedCountry
              ? <CountryAssessment country={selectedCountry} handleBack={handleBack} />
              : (
                <>
                  {years.length > 1 && (
                    <div className="hih-year-selector" role="tablist" aria-label="Assessment year">
                      {years.map((year) => (
                        <button
                          key={year}
                          type="button"
                          role="tab"
                          aria-selected={year === selectedYear}
                          className={`hih-year-tab ${year === selectedYear ? "hih-year-tab-active" : ""}`}
                          onClick={() => handleYearChange(year)}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedYear ? (
                    <>
                      <h2 className="hih-year-section-title">{selectedYear}</h2>
                      <CountriesList groupedCountries={yearGrouped} handleCountryClick={handleCountryClick} />
                    </>
                  ) : (
                    <div className="hih-forum-status">No assessments available yet</div>
                  )}
                </>
              )}
      </article>

      <AnimatePresence>
        {userModalOpen && (
          <UserModal handleClose={() => dispatch(closeUserModal())} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default HihInvestmentForum;
